import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { callFunction } from '../utils/apiClient';

type PlanTier = 'FREE' | 'PRO' | 'ENTERPRISE';

interface PlanLimits {
  maxProjects: number;
  maxStorage: number;
  maxTeamMembers: number;
  maxAICreditsMonthly: number;
  fullReports: boolean;
  fullAIContextExport: boolean;
  prioritySupport: boolean;
}

interface CreditInfo {
  remaining: number;
  total: number;
  resetDate: string | null;
}

interface TokensThisMonth {
  input: number;
  output: number;
  total: number;
}

interface UsageInfo {
  projectCount: number;
  aiUsageThisMonth: number;
  tokensThisMonth: TokensThisMonth;
  storageUsedMB: number;
}

interface PlanContextType {
  planTier: PlanTier;
  planName: string;
  limits: PlanLimits;
  credits: CreditInfo;
  usage: UsageInfo;
  loading: boolean;
  canPerformAction: (action: 'ai' | 'create_project' | 'invite_member') => boolean;
  refreshPlan: () => Promise<void>;
  updateCredits: (remaining: number) => void;
  showUpgrade: boolean;
  upgradeFeature: string;
  openUpgradePrompt: (feature: string) => void;
  closeUpgradePrompt: () => void;
}

const DEFAULT_LIMITS: PlanLimits = {
  maxProjects: 5,
  maxStorage: 100,
  maxTeamMembers: 5,
  maxAICreditsMonthly: 50,
  fullReports: false,
  fullAIContextExport: false,
  prioritySupport: false,
};

const DEFAULT_CONTEXT: PlanContextType = {
  planTier: 'FREE',
  planName: 'Free',
  limits: DEFAULT_LIMITS,
  credits: { remaining: 50, total: 50, resetDate: null },
  usage: { projectCount: 0, aiUsageThisMonth: 0, tokensThisMonth: { input: 0, output: 0, total: 0 }, storageUsedMB: 0 },
  loading: true,
  canPerformAction: () => true,
  refreshPlan: async () => {},
  updateCredits: () => {},
  showUpgrade: false,
  upgradeFeature: '',
  openUpgradePrompt: () => {},
  closeUpgradePrompt: () => {},
};

const PlanContext = createContext<PlanContextType>(DEFAULT_CONTEXT);

export const usePlan = () => useContext(PlanContext);

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [planTier, setPlanTier] = useState<PlanTier>('FREE');
  const [planName, setPlanName] = useState('Free');
  const [limits, setLimits] = useState<PlanLimits>(DEFAULT_LIMITS);
  const [credits, setCredits] = useState<CreditInfo>({ remaining: 50, total: 50, resetDate: null });
  const [usage, setUsage] = useState<UsageInfo>({ projectCount: 0, aiUsageThisMonth: 0, tokensThisMonth: { input: 0, output: 0, total: 0 }, storageUsedMB: 0 });
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const fetchedRef = useRef(false);

  const refreshPlan = useCallback(async () => {
    if (!user) return;

    try {
      const data = await callFunction('getUserPlan', {});

      setPlanTier(data.planTier || 'FREE');
      setPlanName(data.planName || 'Free');
      setLimits(data.limits || DEFAULT_LIMITS);
      setCredits(data.credits || { remaining: 50, total: 50, resetDate: null });
      setUsage(data.usage || { projectCount: 0, aiUsageThisMonth: 0, tokensThisMonth: { input: 0, output: 0, total: 0 }, storageUsedMB: 0 });
    } catch (err) {
      console.warn('[PlanContext] Failed to fetch plan info:', err);
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchedRef.current = true;
      refreshPlan();
    }
    if (!user) {
      fetchedRef.current = false;
      setLoading(false);
    }
  }, [user, refreshPlan]);

  const canPerformAction = useCallback((action: 'ai' | 'create_project' | 'invite_member') => {
    if (planTier === 'ENTERPRISE') return true;

    switch (action) {
      case 'ai':
        return credits.remaining > 0;
      case 'create_project':
        return limits.maxProjects === -1 || usage.projectCount < limits.maxProjects;
      case 'invite_member':
        return true; // Per-project check happens on backend
      default:
        return true;
    }
  }, [planTier, credits, limits, usage]);

  const updateCredits = useCallback((remaining: number) => {
    setCredits(prev => ({ ...prev, remaining }));
  }, []);

  const openUpgradePrompt = useCallback((feature: string) => {
    setUpgradeFeature(feature);
    setShowUpgrade(true);
  }, []);

  const closeUpgradePrompt = useCallback(() => {
    setShowUpgrade(false);
    setUpgradeFeature('');
  }, []);

  return (
    <PlanContext.Provider value={{
      planTier,
      planName,
      limits,
      credits,
      usage,
      loading,
      canPerformAction,
      refreshPlan,
      updateCredits,
      showUpgrade,
      upgradeFeature,
      openUpgradePrompt,
      closeUpgradePrompt,
    }}>
      {children}
    </PlanContext.Provider>
  );
};
